import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Calendar, Clock, ArrowLeft, MessageCircle, Share2, ArrowUpRight } from "lucide-react";
import { findPost, fetchDynamicPost, POSTS } from "../blog/posts";
import MarketingShell from "../components/marketing/MarketingShell";

export default function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const staticPost = findPost(slug);
  const [dynamicPost, setDynamicPost] = useState(null);
  const [loadingDynamic, setLoadingDynamic] = useState(!staticPost);

  // If the slug isn't in the static list, try to fetch it from the API
  // (i.e., an AI-generated nightly article).
  useEffect(() => {
    if (staticPost) return;
    let cancelled = false;
    setLoadingDynamic(true);
    fetchDynamicPost(slug)
      .then((p) => { if (!cancelled) setDynamicPost(p); })
      .finally(() => { if (!cancelled) setLoadingDynamic(false); });
    return () => { cancelled = true; };
  }, [slug, staticPost]);

  const post = staticPost || dynamicPost;

  useEffect(() => {
    if (!post) return;
    document.title = `${post.title} | EcomCatalog AI Blog`;
    // Inject SEO meta tags dynamically — these are what Googlebot reads.
    setMeta("description", post.description);
    setMeta("keywords", post.keywords);
    setMeta("og:title", post.title, true);
    setMeta("og:description", post.description, true);
    setMeta("og:image", post.cover, true);
    setMeta("og:type", "article", true);
    setMeta("og:url", `https://ecomcatalog.ai/blog/${post.slug}`, true);
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", post.title);
    setMeta("twitter:description", post.description);
    setMeta("twitter:image", post.cover);
    setCanonical(`https://ecomcatalog.ai/blog/${post.slug}`);
    // Article JSON-LD structured data — Google rich snippets.
    injectJsonLd({
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "headline": post.title,
      "image": [post.cover],
      "datePublished": post.date,
      "dateModified": post.date,
      "author": { "@type": "Organization", "name": post.author },
      "publisher": {
        "@type": "Organization",
        "name": "EcomCatalog AI",
        "logo": { "@type": "ImageObject", "url": "https://ecomcatalog.ai/logo.png" },
      },
      "description": post.description,
      "mainEntityOfPage": `https://ecomcatalog.ai/blog/${post.slug}`,
      "speakable": {
        "@type": "SpeakableSpecification",
        "cssSelector": ["h1", "h2", "h3", ".blog-summary"],
      },
      "about": (post.keywords || "").split(",").map((k) => k.trim()).filter(Boolean).slice(0, 8).map((name) => ({ "@type": "Thing", "name": name })),
    });
    // Breadcrumb JSON-LD — ChatGPT + Google use this for sitelink-style citation.
    injectJsonLd({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://ecomcatalog.ai/" },
        { "@type": "ListItem", "position": 2, "name": "Blog", "item": "https://ecomcatalog.ai/blog" },
        { "@type": "ListItem", "position": 3, "name": post.title, "item": `https://ecomcatalog.ai/blog/${post.slug}` },
      ],
    }, "breadcrumb-jsonld");
    // FAQPage JSON-LD — only when the post body has h2/h3 sections we
    // can promote as Q&A. For static posts we synthesise FAQ from the
    // h2 headings + the following paragraph. AI engines (ChatGPT,
    // Gemini, Perplexity) cite FAQPage rows verbatim.
    const faqEntries = buildFaq(post);
    if (faqEntries.length > 0) {
      injectJsonLd({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": faqEntries.map((f) => ({
          "@type": "Question",
          "name": f.q,
          "acceptedAnswer": { "@type": "Answer", "text": f.a },
        })),
      }, "faq-jsonld");
    }
  }, [post]);

  if (!post) {
    return (
      <MarketingShell>
        <div className="min-h-[60vh] grid place-items-center">
          <div className="text-center">
            {loadingDynamic ? (
              <h2 className="font-mkt-display text-2xl font-semibold text-slate-300">
                Loading article…
              </h2>
            ) : (
              <>
                <h2 className="font-mkt-display text-3xl font-semibold">Article not found</h2>
                <button onClick={() => navigate("/blog")} className="mt-5 mkt-btn-ghost">
                  <ArrowLeft size={14} /> Back to blog
                </button>
              </>
            )}
          </div>
        </div>
      </MarketingShell>
    );
  }

  const related = POSTS.filter((p) => p.slug !== post.slug).slice(0, 3);
  const shareUrl = typeof window !== "undefined" ? window.location.href : "";

  return (
    <MarketingShell>
      <article className="max-w-3xl mx-auto px-5 sm:px-8 py-16">
        <Link to="/blog" className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-white">
          <ArrowLeft size={14} /> All articles
        </Link>
        <div className="text-[10px] uppercase tracking-[0.3em] text-indigo-400 font-bold mt-8">Article</div>
        <h1 className="font-mkt-display text-4xl md:text-5xl font-semibold tracking-tighter mt-3 leading-[1.05]">
          {post.title}
        </h1>
        <div className="flex items-center gap-4 text-[12px] text-slate-500 mt-5">
          <span className="inline-flex items-center gap-1"><Calendar size={12} /> {post.date}</span>
          <span className="inline-flex items-center gap-1"><Clock size={12} /> {post.readMin} min read</span>
          <span>· by {post.author}</span>
        </div>

        {post.cover && (
          <img
            src={post.cover}
            alt={post.title}
            className="w-full rounded-2xl mt-8 mb-10 border border-white/[0.06]"
          />
        )}

        <div className="prose-content space-y-5">
          {post.dynamic && post.bodyHtml ? (
            <>
              <div
                className="blog-html-body text-base text-slate-300 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: post.bodyHtml }}
              />
              {post.faq && post.faq.length > 0 && (
                <div className="mt-12">
                  <h2 className="font-mkt-display text-2xl md:text-3xl font-semibold tracking-tight text-white mb-4">
                    Frequently Asked Questions
                  </h2>
                  <div className="space-y-4">
                    {post.faq.map((f, i) => (
                      <details
                        key={i}
                        className="mkt-glass p-5 group cursor-pointer"
                      >
                        <summary className="font-semibold text-white text-base list-none flex items-center justify-between gap-3">
                          <span>{f.q}</span>
                          <span className="text-indigo-300 group-open:rotate-45 transition-transform">+</span>
                        </summary>
                        <p className="text-slate-300 leading-relaxed mt-3">{f.a}</p>
                      </details>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            (post.body || []).map((blk, i) => {
              if (blk.h === "h2") {
                return (
                  <h2 key={i} className="font-mkt-display text-2xl md:text-3xl font-semibold tracking-tight mt-10 text-white">
                    {blk.t}
                  </h2>
                );
              }
              if (blk.h === "h3") {
                return (
                  <h3 key={i} className="font-mkt-display text-xl font-semibold mt-6">
                    {blk.t}
                  </h3>
                );
              }
              return (
                <p key={i} className="text-base text-slate-300 leading-relaxed">
                  {blk.p}
                </p>
              );
            })
          )}
        </div>

        <div className="mt-12 flex flex-wrap gap-3">
          <a
            href={`https://wa.me/?text=${encodeURIComponent(`${post.title} — ${shareUrl}`)}`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#25D366] hover:bg-[#1FBA56] text-black text-xs font-semibold"
          >
            <MessageCircle size={12} /> Share on WhatsApp
          </a>
          <button
            onClick={() => { navigator.clipboard.writeText(shareUrl); }}
            className="mkt-btn-ghost text-xs"
          >
            <Share2 size={12} /> Copy link
          </button>
        </div>

        <div className="mt-16 mkt-glass-strong p-8 relative overflow-hidden">
          <div className="absolute -top-20 -right-20 w-60 h-60 rounded-full bg-indigo-500/15 blur-3xl pointer-events-none" />
          <div className="relative">
            <h3 className="font-mkt-display text-2xl font-semibold tracking-tight">
              Try EcomCatalog AI.
            </h3>
            <p className="text-sm text-slate-400 mt-2">
              List on Myntra in 90 seconds with AI auto-fill. Talk to our team to get onboarded.
            </p>
            <Link to="/login" className="mkt-btn-gradient mt-5">
              Get Started <ArrowUpRight size={14} />
            </Link>
          </div>
        </div>

        {related.length > 0 && (
          <section className="mt-20">
            <h3 className="font-mkt-display text-2xl font-semibold tracking-tight mb-6">
              More reading
            </h3>
            <div className="grid md:grid-cols-3 gap-4">
              {related.map((r) => (
                <Link
                  key={r.slug}
                  to={`/blog/${r.slug}`}
                  className="mkt-glass mkt-hover-lift p-5 block"
                >
                  <div className="text-[10px] uppercase tracking-[0.2em] text-indigo-300 font-bold mb-1">{r.date}</div>
                  <div className="font-mkt-display text-base font-semibold leading-tight">{r.title}</div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </article>
    </MarketingShell>
  );
}

/* ─── small DOM helpers — keep meta tags fresh as user navigates ──── */
function setMeta(name, content, isProperty = false) {
  if (typeof document === "undefined") return;
  const attr = isProperty ? "property" : "name";
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content || "");
}
function setCanonical(href) {
  if (typeof document === "undefined") return;
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}
function injectJsonLd(data, id = "article-jsonld") {
  if (typeof document === "undefined") return;
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

/**
 * Build a FAQPage entity from a post.
 * - If the post already carries `faq[]` (dynamic articles), use it.
 * - Otherwise synthesise Q&A by promoting H2 headings to questions
 *   and the following paragraph to the answer. Skips H2s that are
 *   pure CTAs ("Try EcomCatalog AI", "Get started").
 */
function buildFaq(post) {
  if (Array.isArray(post.faq) && post.faq.length > 0) {
    return post.faq.map((f) => ({ q: f.q || f.question, a: f.a || f.answer })).filter((f) => f.q && f.a);
  }
  if (!Array.isArray(post.body)) return [];
  const items = [];
  for (let i = 0; i < post.body.length; i++) {
    const node = post.body[i];
    if (node.h === "h2" && typeof node.t === "string") {
      const t = node.t.trim();
      // Skip CTA / soft-sell headings
      if (/^(try|get started|sign up|book a demo)/i.test(t)) continue;
      // Find the next paragraph
      const next = post.body[i + 1];
      if (next && next.p) {
        // Convert imperative heading into a question shape when possible
        let q = t.endsWith("?") ? t : t.replace(/\.$/, "") + "?";
        // Strip leading "How", "Why" preamble if the heading is already a question
        items.push({ q, a: next.p });
      }
    }
  }
  return items.slice(0, 8);
}
